import { spinner } from "@clack/prompts";
import { existsSync, readdirSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import { z } from "incur";
import { createApp, getApp, type WhopApp } from "./api.js";
import { slugify, writeAppConfig, type AppConfig } from "./config.js";
import { extractSourceArchive } from "./source-archive.js";
import { commitAll, gitInit, gitInstalled, repoToplevel } from "./git.js";
import {
	runTanstackCreate,
	whopifyProject,
	type PackageManager,
} from "./scaffold.js";
import {
	CommandAbort,
	cancelled,
	downloadArchive,
	ensurePackageManager,
	log,
	promptForAppType,
	promptForNewApp,
	promptText,
	runInstall,
	type CommandContext,
	type NewAppType,
} from "./shared.js";

export const initOptions = z.object({
	name: z
		.string()
		.optional()
		.describe("Name for the new app (prompted for interactively if omitted)"),
	route: z
		.string()
		.optional()
		.describe(
			"Route the app is served from (<route>.whop.site). Defaults to a slug of the name",
		),
	template: z
		.string()
		.optional()
		.describe(
			"Clone this app's published source archive (app_xxx) instead of the blank TanStack Start template",
		),
	dir: z
		.string()
		.optional()
		.describe("Target directory (defaults to ./<route>)"),
	app_type: z
		.enum(["b2c_app", "website"])
		.optional()
		.describe(
			"What you are building: b2c_app (creators install it into their whop) or website (visitors browse it at your route). Asked interactively if omitted, and required when there is nobody to ask",
		),
	company_id: z
		.string()
		.optional()
		.describe(
			"Company the app belongs to (biz_xxx). Defaults to the active account, and is required when the credential has none",
		),
});

export const initOutput = z.object({
	app_id: z.string(),
	name: z.string(),
	route: z.string(),
	dir: z.string(),
	manual_steps: z.array(z.string()).optional(),
});

type InitOptions = z.infer<typeof initOptions>;

export type InitResult = {
	projectDir: string;
	config: AppConfig;
	manualSteps: string[];
};

/**
 * Asks what is being built, or stops. There is no safe guess: a website can
 * never be turned back into an app, and an app saved as the wrong kind never
 * shows up where its owner looks for it.
 */
async function resolveAppType(
	c: CommandContext,
	name: string,
	route: string,
): Promise<NewAppType> {
	if (!c.agent) return promptForAppType();

	throw new CommandAbort({
		code: "APP_TYPE_REQUIRED",
		message: [
			"Nothing was created. Ask the user what they are building, then re-run with --app_type:",
			"",
			`  whop apps init --name "${name}" --route ${route} --app_type b2c_app   (creators install it into their whop)`,
			`  whop apps init --name "${name}" --route ${route} --app_type website   (visitors browse it at ${route}.whop.site)`,
			"",
			"Ask rather than guess: a website cannot be turned back into an app later.",
		].join("\n"),
		retryable: true,
		cta: {
			commands: [
				{
					command: "apps init",
					description: "Re-run with --app_type b2c_app or --app_type website",
				},
			],
		},
	});
}

async function resolveNameAndRoute(
	c: CommandContext,
	options: Pick<InitOptions, "name" | "route" | "app_type">,
	templateAppType?: NewAppType,
): Promise<{ name: string; route: string; appType: NewAppType }> {
	// The flag beats the template it was cloned from: it is the person saying
	// what they want, not a guess from what they copied.
	const knownAppType = options.app_type ?? templateAppType;

	if (options.name) {
		const route = slugify(options.route ?? options.name);
		if (!route) {
			throw new CommandAbort({
				code: "ROUTE_REQUIRED",
				message: `Couldn't derive a route from "${options.route ?? options.name}" — use letters and numbers (e.g. my-site).`,
				retryable: true,
			});
		}
		const appType =
			knownAppType ?? (await resolveAppType(c, options.name, route));
		return { name: options.name, route, appType };
	}

	if (c.agent) {
		throw new CommandAbort({
			code: "NAME_REQUIRED",
			message: [
				"Ask the user three things, then re-run with flags:",
				"  1. What the app is called.",
				"  2. Which route it should live at (the route becomes <route>.whop.site).",
				"  3. Whether they are building an app creators install into their whop, or a website visitors browse.",
				"",
				'  whop apps init --name "My Site" --route my-site --app_type website',
				"",
				"--app_type takes b2c_app or website, and is required here. Ask rather than guess: a website cannot be turned back into an app later.",
			].join("\n"),
			retryable: true,
			cta: {
				commands: [
					{
						command: "apps init",
						description:
							"Re-run with --name and --app_type (and optionally --route)",
					},
				],
			},
		});
	}

	if (options.route) {
		const appType = knownAppType ?? (await promptForAppType());
		const name = await promptText("What is your app called?", "My Site");
		if (name === null) cancelled();
		const route = slugify(options.route);
		if (!route) {
			throw new CommandAbort({
				code: "ROUTE_REQUIRED",
				message: `Couldn't derive a route from "${options.route}" — use letters and numbers (e.g. my-site).`,
				retryable: true,
			});
		}
		return { name, route, appType };
	}

	return promptForNewApp(knownAppType);
}

function ensureEmptyTarget(targetDir: string): void {
	if (existsSync(targetDir) && readdirSync(targetDir).length > 0) {
		throw new CommandAbort({
			code: "TARGET_NOT_EMPTY",
			message: `Target directory is not empty: ${targetDir}. Pick a different route or pass --dir <empty-dir>. To work on an existing app's code, use \`whop apps pull\`.`,
		});
	}
}

async function fetchTemplateSource(
	templateId: string,
): Promise<{ template: WhopApp; sourceUrl: string }> {
	let template: WhopApp;
	try {
		template = await getApp(templateId);
	} catch (err) {
		throw new CommandAbort({
			code: "TEMPLATE_NOT_FOUND",
			message: `Could not load template app ${templateId}: ${err instanceof Error ? err.message : "unknown error"}`,
			retryable: true,
		});
	}
	const sourceUrl = template.production_web_build?.source_url;
	if (!sourceUrl) {
		throw new CommandAbort({
			code: "TEMPLATE_HAS_NO_SOURCE",
			message: `${template.name} (${template.id}) has no published source archive, so it can't be used as a template.`,
		});
	}
	return { template, sourceUrl };
}

/**
 * The full init flow: register the app on Whop (claiming the route before any
 * files are written, so a taken route fails fast), materialize the project —
 * the latest TanStack Start template, or a template app's published source —
 * wire it for Whop hosting, install dependencies, and git-init with an
 * initial commit. Shared with `deploy`'s interactive fallback for
 * not-yet-a-project directories.
 */
export async function runInitFlow(
	c: CommandContext,
	options: InitOptions,
	pm: PackageManager = ensurePackageManager(),
): Promise<InitResult> {
	const templateSource = options.template
		? await fetchTemplateSource(options.template)
		: null;

	// A clone is the same kind of app as the source it was copied from, so
	// there is nothing to ask. Only `website` is inherited, because a website
	// can never be retyped — any other source still asks, or takes the default.
	const templateAppType =
		templateSource?.template.app_type === "website" ? "website" : undefined;

	const { name, route, appType } = await resolveNameAndRoute(
		c,
		options,
		templateAppType,
	);
	const targetDir = resolve(options.dir ?? `./${route}`);
	ensureEmptyTarget(targetDir);

	let app: WhopApp;
	try {
		app = await createApp({
			name,
			route,
			app_type: appType,
			company_id: options.company_id,
		});
	} catch (err) {
		throw new CommandAbort({
			code: "CREATE_FAILED",
			message:
				err instanceof Error ? err.message : "Failed to register the app",
			retryable: true,
		});
	}
	const config: AppConfig = { app_id: app.id, name, route };

	const spin = c.agent ? null : spinner();
	if (templateSource) {
		spin?.start(
			`Cloning ${templateSource.template.name} (${templateSource.template.id}) source archive`,
		);
		try {
			const zip = await downloadArchive(templateSource.sourceUrl);
			const fileCount = extractSourceArchive(zip, targetDir);
			spin?.stop(
				`Cloned ${fileCount} files from ${templateSource.template.name} → ${targetDir}`,
			);
		} catch (err) {
			spin?.error("Template clone failed");
			throw new CommandAbort({
				code: "TEMPLATE_CLONE_FAILED",
				message: `${err instanceof Error ? err.message : "Failed to clone the template source"}\n\nThe app ${app.id} is already registered — re-run \`whop apps init --template ${options.template} --dir ${targetDir}\` to retry.`,
				retryable: true,
			});
		}
	} else {
		spin?.start("Scaffolding the latest TanStack Start template");
		try {
			runTanstackCreate({ targetDir, projectName: route, pm });
			spin?.stop(`Scaffolded ${route} from the latest TanStack Start template`);
		} catch (err) {
			spin?.error("Scaffold failed");
			throw new CommandAbort({
				code: "SCAFFOLD_FAILED",
				message: `${err instanceof Error ? err.message : "Failed to scaffold the project"}\n\nThe app ${app.id} is already registered — fix the problem and re-run \`whop apps init --name "${name}" --route ${route} --app_type ${appType}\`.`,
			});
		}
	}

	// The template's own link config rides along in its source archive —
	// overwrite it so this project deploys to the new app, not the template.
	writeAppConfig(targetDir, config);

	const { manualSteps } = whopifyProject({ targetDir, route });

	try {
		runInstall(c, targetDir);
	} catch (err) {
		throw new CommandAbort({
			code: "INSTALL_FAILED",
			message: err instanceof Error ? err.message : "Install failed",
			retryable: true,
		});
	}

	initGitRepo(c, targetDir);

	return { projectDir: targetDir, config, manualSteps };
}

/**
 * Scaffolds get a git repo with an initial commit, like every modern create-*
 * tool — it is also what lets `whop apps pull` merge later. Skipped inside an
 * existing repo (the project is already under version control) and when git
 * isn't installed; only `pull` hard-requires git.
 */
function initGitRepo(c: CommandContext, targetDir: string): void {
	if (!gitInstalled()) {
		log(c, chalk.yellow("git is not installed — skipping repository setup."));
		return;
	}
	if (repoToplevel(targetDir)) return;
	try {
		gitInit(targetDir);
		commitAll(targetDir, "Initial commit from whop apps init");
	} catch (err) {
		log(
			c,
			chalk.yellow(
				`Couldn't create the git repository (${err instanceof Error ? err.message : "unknown error"}) — continuing without one.`,
			),
		);
	}
}
